<script setup lang="ts">
import type { FriendLinkRSSItem } from '~/types/blog'
import { resolveImageAssetUrl } from '~/composables/useAssetUrl'
import { compactDisplayUrl, formatDisplayDate } from '~/composables/useDisplayText'

const props = withDefaults(
  defineProps<{
    items: FriendLinkRSSItem[]
    total?: number
    hasMore?: boolean
    loadingMore?: boolean
  }>(),
  {
    total: 0,
    hasMore: false,
    loadingMore: false
  }
)

const emit = defineEmits<{
  (e: 'load-more'): void
}>()

const sentinel = ref<HTMLElement | null>(null)
let observer: IntersectionObserver | null = null
const featuredTitle = ref<HTMLElement | null>(null)
const hasSingleLineFeaturedTitle = ref(false)
let featuredTitleObserver: ResizeObserver | null = null

const hasHeroLayout = computed(() => props.total >= 3 && props.items.length >= 3)
const featuredItem = computed(() => hasHeroLayout.value ? (props.items[0] || null) : null)
const secondaryItems = computed(() => hasHeroLayout.value ? props.items.slice(1, 3) : [])
const feedItems = computed(() => hasHeroLayout.value ? props.items.slice(3) : props.items)
const showTerminalState = computed(() => feedItems.value.length > 0 && !props.hasMore && !props.loadingMore)
// 根据标题实际占用的行数，为摘要保留相应的可用空间。
const updateFeaturedSummaryLines = () => {
  const title = featuredTitle.value
  if (!import.meta.client || !title) {
    return
  }
  const lineHeight = Number.parseFloat(getComputedStyle(title).lineHeight)
  if (!lineHeight) {
    return
  }
  const titleLines = Math.min(2, Math.max(1, Math.round(title.getBoundingClientRect().height / lineHeight)))
  hasSingleLineFeaturedTitle.value = titleLines === 1
}
const sourceKey = (item: FriendLinkRSSItem) => `${item.sourceName || ''}:${item.sourceURL || ''}`
const titleSources = computed(() => {
  const seen = new Set<string>()
  const result: FriendLinkRSSItem[] = []
  props.items.forEach(item => {
    const key = sourceKey(item)
    if (!key || seen.has(key)) {
      return
    }
    seen.add(key)
    result.push(item)
  })
  return result.slice(0, 3)
})
const sourceMonogram = (item: FriendLinkRSSItem) => {
  const text = (item.sourceName || item.sourceURL || '?').trim()
  return text.slice(0, 1).toUpperCase()
}
const bindObserver = () => {
  if (!import.meta.client) return
  observer?.disconnect()
  observer = null
  if (!sentinel.value || !props.hasMore) {
    return
  }
  observer = new IntersectionObserver(
    entries => {
      if (entries[0]?.isIntersecting) {
        emit('load-more')
      }
    },
    { rootMargin: '280px 0px' }
  )
  observer.observe(sentinel.value)
}

onMounted(bindObserver)
watch([sentinel, () => props.hasMore], () => bindObserver())
watch(featuredTitle, title => {
  featuredTitleObserver?.disconnect()
  featuredTitleObserver = null
  if (!import.meta.client || !title) {
    return
  }
  featuredTitleObserver = new ResizeObserver(updateFeaturedSummaryLines)
  featuredTitleObserver.observe(title)
  updateFeaturedSummaryLines()
})

onBeforeUnmount(() => {
  observer?.disconnect()
  featuredTitleObserver?.disconnect()
})
</script>

<template>
  <section class="card rss-section">
    <div class="rss-head">
      <div class="rss-title-row">
        <div class="rss-source-stack" aria-hidden="true">
          <span v-if="!titleSources.length" class="rss-title-icon">
            <i class="fa-solid fa-rss" aria-hidden="true" />
          </span>
          <span v-for="item in titleSources" :key="sourceKey(item)" class="rss-source-avatar">
            <BlogImage
              v-if="item.sourceLogo"
              :src="resolveImageAssetUrl(item.sourceLogo)"
              :alt="item.sourceName || 'RSS source logo'"
            />
            <span v-else>{{ sourceMonogram(item) }}</span>
          </span>
        </div>
        <div class="rss-title-copy">
          <h2>朋友圈</h2>
          <p>TA 们最近在写什么</p>
        </div>
      </div>
      <span v-if="total" class="rss-title-count">{{ total }} 条动态</span>
    </div>

    <div v-if="hasHeroLayout && featuredItem" class="rss-hero-grid">
      <a
        class="rss-featured-card"
        :href="featuredItem.url"
        target="_blank"
        rel="noopener noreferrer"
      >
        <span class="rss-featured-badge">最新内容</span>
        <div class="rss-featured-eyebrow">
          <div class="rss-featured-blog">
            <BlogImage
              v-if="featuredItem.sourceLogo"
              class="rss-featured-blog-logo"
              :src="resolveImageAssetUrl(featuredItem.sourceLogo)"
              :alt="featuredItem.sourceName || 'RSS source logo'"
            />
            <span v-else class="rss-featured-blog-fallback">{{ sourceMonogram(featuredItem) }}</span>
            <div class="rss-featured-blog-copy">
              <span class="rss-featured-blog-name">{{ featuredItem.sourceName || featuredItem.sourceURL }}</span>
              <span class="rss-featured-blog-meta">{{ compactDisplayUrl(featuredItem.sourceURL) }}</span>
            </div>
          </div>
        </div>

        <div class="rss-featured-main">
          <h3 ref="featuredTitle" class="rss-featured-title">{{ featuredItem.title }}</h3>
          <p
            v-if="featuredItem.summary"
            class="rss-featured-summary"
            :class="{ 'rss-featured-summary--four-lines': hasSingleLineFeaturedTitle }"
          >
            {{ featuredItem.summary }}
          </p>
          <div v-else class="rss-summary-empty" aria-label="暂无摘要">
            <span />
            <span />
            <span />
          </div>
        </div>

        <div class="rss-featured-footer">
          <time class="rss-featured-time">
            <i class="fa-regular fa-clock" aria-hidden="true" />
            {{ formatDisplayDate(featuredItem.publishedAt) }}
          </time>
          <span class="rss-featured-cta">
            <i class="fa-solid fa-arrow-up-right-from-square" aria-hidden="true" />
            查看原文
          </span>
        </div>
      </a>

      <div class="rss-secondary-list">
        <a
          v-for="(item, idx) in secondaryItems"
          :key="`${item.linkId}-${item.url}-${idx}`"
          class="rss-secondary-card"
          :href="item.url"
          target="_blank"
          rel="noopener noreferrer"
        >
          <div class="rss-secondary-head">
            <BlogImage
              v-if="item.sourceLogo"
              class="rss-secondary-logo"
              :src="resolveImageAssetUrl(item.sourceLogo)"
              :alt="item.sourceName || 'RSS source logo'"
            />
            <span v-else class="rss-secondary-fallback">{{ sourceMonogram(item) }}</span>
            <strong>{{ item.title }}</strong>
          </div>
          <div class="rss-secondary-summary-area">
            <p v-if="item.summary" class="rss-secondary-summary">
              {{ item.summary }}
            </p>
            <div v-else class="rss-summary-empty" aria-label="暂无摘要">
              <span />
              <span />
              <span />
            </div>
          </div>
          <div class="rss-secondary-meta">
            <time>
              <i class="fa-regular fa-clock" aria-hidden="true" />
              {{ formatDisplayDate(item.publishedAt) }}
            </time>
            <span>{{ item.sourceName || item.sourceURL }}</span>
          </div>
        </a>
      </div>
    </div>

    <div v-if="feedItems.length" class="rss-stream">
      <div v-if="hasHeroLayout" class="rss-stream-divider">
        <span>
          <i class="fa-regular fa-compass" aria-hidden="true" />
          探索更多
        </span>
      </div>
      <div class="rss-timeline">
        <article
          v-for="(item, idx) in feedItems"
          :key="`${item.linkId}-${item.url}-stream-${idx}`"
          class="rss-timeline-item"
        >
          <div class="rss-timeline-dot" />
          <a
            class="rss-timeline-card"
            :href="item.url"
            target="_blank"
            rel="noopener noreferrer"
          >
            <div class="rss-timeline-meta">
              <div class="rss-timeline-source">
                <BlogImage
                  v-if="item.sourceLogo"
                  class="rss-timeline-logo"
                  :src="resolveImageAssetUrl(item.sourceLogo)"
                  :alt="item.sourceName || 'RSS source logo'"
                />
                <span v-else class="rss-timeline-fallback">{{ sourceMonogram(item) }}</span>
                <span class="rss-timeline-source-name">{{ item.sourceName || item.sourceURL }}</span>
              </div>
              <time class="rss-timeline-time">
                <i class="fa-regular fa-clock" aria-hidden="true" />
                {{ formatDisplayDate(item.publishedAt) }}
              </time>
            </div>
            <strong class="rss-timeline-title">{{ item.title }}</strong>
            <p v-if="item.summary" class="rss-timeline-summary">{{ item.summary }}</p>
          </a>
        </article>
      </div>
    </div>

    <div v-else-if="!props.items.length" class="empty-card">暂未收录 RSS 更新</div>

    <div v-if="props.hasMore || props.loadingMore || showTerminalState" class="load-footer">
      <div v-if="props.hasMore" ref="sentinel" class="scroll-sentinel" />
      <BlogLoadingIndicator v-if="props.loadingMore" />
      <BlogListEnd v-else-if="showTerminalState" />
    </div>
  </section>
</template>

<style scoped>
.rss-section {
  position: relative;
  display: grid;
  gap: 14px;
  padding: 18px;
}

.rss-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  min-height: 34px;
  padding-right: 112px;
}

.rss-title-row {
  min-width: 0;
  display: flex;
  align-items: center;
  gap: 12px;
}

.rss-source-stack {
  display: flex;
  align-items: center;
  flex-shrink: 0;
  padding-left: 8px;
}

.rss-source-avatar,
.rss-title-icon {
  width: 40px;
  height: 40px;
  border: 2px solid var(--bg);
  border-radius: 999px;
  background: var(--bg-soft);
  box-shadow: 0 4px 10px rgba(15, 23, 42, 0.08);
  overflow: hidden;
}

.rss-source-avatar {
  display: inline-grid;
  place-items: center;
  margin-left: -8px;
}

.rss-source-avatar img {
  display: block;
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.rss-source-avatar span,
.rss-title-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  color: var(--brand);
  font-size: 14px;
  font-weight: 700;
  background: color-mix(in srgb, var(--brand) 12%, var(--bg-soft));
}

.rss-title-copy {
  min-width: 0;
  display: grid;
  gap: 2px;
}

.rss-head h2 {
  margin: 0;
  font-size: 22px;
  line-height: 1.18;
}

.rss-title-copy p {
  margin: 0;
  color: var(--text-soft);
  font-size: 13px;
}

.rss-title-count {
  position: absolute;
  top: 0;
  right: 0;
  display: inline-flex;
  align-items: center;
  padding: 10px 14px 9px 18px;
  border-radius: 0 var(--radius) 0 var(--radius);
  color: color-mix(in srgb, var(--brand) 82%, var(--text));
  background:
    linear-gradient(135deg, color-mix(in srgb, var(--brand) 16%, transparent) 0%, color-mix(in srgb, var(--brand) 9%, transparent) 100%);
  font-size: 12px;
  font-weight: 600;
  letter-spacing: 0.02em;
}

.rss-hero-grid {
  display: grid;
  grid-template-columns: minmax(0, 1.25fr) minmax(280px, 0.95fr);
  gap: 12px;
  height: 296px;
  min-height: 0;
}

.rss-featured-card,
.rss-secondary-card,
.rss-timeline-card {
  color: var(--text);
  text-decoration: none;
}

.rss-featured-card {
  --rss-featured-accent: color-mix(in srgb, var(--brand) 24%, var(--bg));
  position: relative;
  display: grid;
  grid-template-rows: auto minmax(0, 1fr) 34px;
  gap: 16px;
  min-width: 0;
  min-height: 0;
  padding: 16px 20px 10px;
  border: 4px solid var(--rss-featured-accent);
  border-radius: var(--radius);
  background:
    radial-gradient(circle at top left, color-mix(in srgb, var(--brand) 11%, transparent) 0, transparent 42%),
    linear-gradient(180deg, color-mix(in srgb, var(--brand) 4%, var(--bg)) 0%, var(--bg) 100%);
  transition: transform 0.18s ease;
  overflow: hidden;
}

.rss-featured-card:hover,
.rss-secondary-card:hover,
.rss-timeline-card:hover {
  transform: translateY(-1px);
}

.rss-featured-eyebrow,
.rss-featured-footer {
  display: flex;
  align-items: center;
  gap: 10px;
}

.rss-featured-eyebrow {
  justify-content: space-between;
}

.rss-featured-badge {
  position: absolute;
  top: 0;
  left: 0;
  display: inline-flex;
  align-items: center;
  padding: 5px 18px 5px 14px;
  border-radius: 0 0 var(--radius) 0;
  background: var(--rss-featured-accent);
  color: color-mix(in srgb, var(--brand) 82%, var(--text));
  font-size: 12px;
  font-weight: 600;
  letter-spacing: 0.02em;
}

.rss-featured-main {
  display: grid;
  grid-template-rows: auto minmax(0, 1fr);
  gap: 8px;
  min-height: 0;
}

.rss-featured-blog,
.rss-secondary-head {
  min-width: 0;
}

.rss-featured-blog {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 8px;
  margin-left: auto;
  max-width: 78%;
  color: var(--text);
}

.rss-featured-blog-logo,
.rss-featured-blog-fallback,
.rss-secondary-fallback {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.rss-featured-blog-logo {
  width: 42px;
  height: 42px;
  border-radius: 50%;
  object-fit: cover;
  border: 1px solid color-mix(in srgb, var(--line) 56%, transparent);
}

.rss-featured-blog-fallback,
.rss-secondary-fallback {
  color: var(--brand);
  font-weight: 700;
  background: color-mix(in srgb, var(--brand) 12%, transparent);
}

.rss-featured-blog-fallback {
  width: 42px;
  height: 42px;
  border-radius: 50%;
  font-size: 15px;
}

.rss-featured-blog-copy {
  min-width: 0;
  display: grid;
  gap: 2px;
  text-align: right;
}

.rss-featured-blog-name {
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-size: 19px;
  font-weight: 700;
  line-height: 1.2;
}

.rss-featured-blog-meta {
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  color: var(--text-soft);
  font-size: 14px;
  line-height: 1.25;
}

.rss-featured-card h3 {
  margin: 0;
  font-size: 25px;
  line-height: 1.38;
}

.rss-featured-title {
  max-height: 2.76em;
  display: -webkit-box;
  overflow: hidden;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
}

.rss-featured-summary {
  min-width: 0;
  max-width: 100%;
  min-height: 0;
  margin: 0;
  color: var(--text-soft);
  font-size: 16px;
  line-height: 25px;
  max-height: 75px;
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;
  overflow-wrap: anywhere;
}

.rss-featured-summary--four-lines {
  max-height: 100px;
  -webkit-line-clamp: 4;
}

.rss-summary-empty {
  display: grid;
  align-content: center;
  gap: 8px;
  min-height: 0;
}

.rss-summary-empty span {
  height: 10px;
  border-radius: 999px;
  background: color-mix(in srgb, var(--line) 68%, transparent);
}

.rss-summary-empty span:nth-child(1) {
  width: 76%;
}

.rss-summary-empty span:nth-child(2) {
  width: 58%;
}

.rss-summary-empty span:nth-child(3) {
  width: 34%;
}

.rss-featured-footer {
  height: 34px;
  padding-top: 10px;
  justify-content: space-between;
  border-top: 1px solid color-mix(in srgb, var(--line) 58%, transparent);
}

.rss-featured-time,
.rss-featured-cta {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  font-size: 12px;
  white-space: nowrap;
  line-height: 1.2;
}

.rss-featured-time i,
.rss-featured-cta i,
.rss-secondary-card time i,
.rss-stream-divider i,
.rss-timeline-time i {
  flex-shrink: 0;
  font-size: 0.92em;
  opacity: 0.82;
}

.rss-featured-time {
  color: var(--text-soft);
}

.rss-featured-cta {
  color: var(--brand);
  font-weight: 600;
}

.rss-secondary-list {
  display: grid;
  grid-template-rows: repeat(2, minmax(0, 1fr));
  gap: 10px;
  min-height: 0;
}

.rss-secondary-card {
  display: grid;
  grid-template-rows: auto minmax(0, 1fr) auto;
  gap: 8px;
  min-width: 0;
  min-height: 0;
  padding: 14px;
  border-radius: var(--radius);
  background: var(--bg);
  transition:
    transform 0.18s ease,
    background-color 0.18s ease;
  overflow: hidden;
}

.rss-secondary-card:hover {
  background: color-mix(in srgb, var(--brand) 3%, var(--bg));
}

.rss-secondary-head {
  display: flex;
  align-items: center;
  gap: 8px;
  min-width: 0;
}

.rss-secondary-head strong {
  font-weight: 600;
  min-width: 0;
  line-height: 22px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.rss-secondary-summary-area {
  display: grid;
  align-content: center;
  min-width: 0;
  min-height: 0;
}

.rss-secondary-logo {
  width: 28px;
  height: 28px;
  border-radius: 50%;
  object-fit: cover;
  flex-shrink: 0;
}

.rss-secondary-fallback {
  width: 28px;
  height: 28px;
  border-radius: 50%;
  font-size: 12px;
}

.rss-secondary-meta {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  color: var(--text-soft);
  font-size: 12px;
}

.rss-secondary-meta > span {
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.rss-secondary-summary {
  margin: 0;
  color: var(--text-soft);
  font-size: 13px;
  line-height: 20px;
  max-height: 40px;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.rss-secondary-card time,
.rss-timeline-time {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  color: var(--text-soft);
  font-size: 12px;
  white-space: nowrap;
}

.rss-stream {
  display: grid;
  gap: 12px;
}

.rss-stream-divider {
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto minmax(0, 1fr);
  align-items: center;
  gap: 12px;
  color: var(--text-soft);
  font-size: 13px;
}

.rss-stream-divider::before,
.rss-stream-divider::after {
  content: '';
  height: 2px;
  background-image: linear-gradient(to right, var(--line) 50%, transparent 0);
  background-size: 8px 2px;
  background-repeat: repeat-x;
}

.rss-stream-divider span {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  white-space: nowrap;
}

.rss-timeline {
  position: relative;
  display: grid;
  gap: 12px;
}

.rss-timeline::before {
  content: '';
  position: absolute;
  left: 10px;
  top: 10px;
  bottom: 10px;
  width: 1px;
  background: var(--line);
}

.rss-timeline-item {
  position: relative;
  display: grid;
  grid-template-columns: 20px minmax(0, 1fr);
  gap: 12px;
  align-items: start;
}

.rss-timeline-dot {
  justify-self: center;
  margin-top: 10px;
  width: 12px;
  height: 12px;
  border: 2px solid var(--brand);
  border-radius: 999px;
  background: var(--bg);
  z-index: 1;
}

.rss-timeline-card {
  display: grid;
  gap: 8px;
  min-width: 0;
  padding: 14px 16px;
  border-radius: var(--radius);
  background: var(--bg);
  transition:
    transform 0.18s ease,
    background-color 0.18s ease;
}

.rss-timeline-card:hover {
  background: color-mix(in srgb, var(--brand) 3%, var(--bg));
}

.rss-timeline-meta {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
}

.rss-timeline-source {
  display: flex;
  align-items: center;
  gap: 7px;
  min-width: 0;
  color: var(--text-soft);
  font-size: 12px;
}

.rss-timeline-logo,
.rss-timeline-fallback {
  width: 22px;
  height: 22px;
  border-radius: 50%;
  flex-shrink: 0;
}

.rss-timeline-logo {
  object-fit: cover;
}

.rss-timeline-fallback {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  color: var(--brand);
  font-size: 10px;
  font-weight: 700;
  background: color-mix(in srgb, var(--brand) 12%, transparent);
}

.rss-timeline-source-name {
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.rss-timeline-title {
  font-weight: 600;
  min-width: 0;
  line-height: 24px;
  max-height: 48px;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.rss-timeline-summary {
  margin: 0;
  color: var(--text-soft);
  font-size: 13px;
  line-height: 21px;
  max-height: 42px;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.scroll-sentinel {
  position: absolute;
  inset: 0 0 auto;
  height: 1px;
}

.load-footer {
  position: relative;
  min-height: 1px;
}

@media (max-width: 900px) {
  .rss-hero-grid {
    grid-template-columns: 1fr;
    height: auto;
  }

  .rss-featured-summary,
  .rss-featured-summary--four-lines {
    max-height: 125px;
    -webkit-line-clamp: 5;
  }
}

@media (max-width: 760px) {
  .rss-section {
    padding: 16px;
  }

  .rss-head {
    align-items: start;
    flex-direction: column;
    gap: 10px;
    padding-right: 96px;
  }

  .rss-title-row {
    width: 100%;
    gap: 10px;
  }

  .rss-source-avatar,
  .rss-title-icon {
    width: 36px;
    height: 36px;
  }

  .rss-head h2 {
    font-size: 21px;
  }

  .rss-title-count {
    padding: 9px 12px 8px 15px;
  }

  .rss-featured-card h3 {
    font-size: 22px;
  }

  .rss-featured-eyebrow,
  .rss-timeline-meta {
    align-items: start;
  }

  .rss-featured-blog {
    max-width: 76%;
  }

  .rss-featured-blog-logo,
  .rss-featured-blog-fallback {
    width: 38px;
    height: 38px;
  }

  .rss-featured-blog-name {
    font-size: 17px;
  }

  .rss-featured-blog-meta {
    font-size: 13px;
  }

  .rss-timeline-source {
    white-space: nowrap;
  }
}
</style>
